import React, {
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import { fetchViaPost } from "../helpers/fetchViaPost";

async function callUserFunction(
  functionName,
  body = {}
) {
  const response = await fetchViaPost(
    functionName,
    JSON.stringify(body)
  );

  return response.json();
}

export function AdminUsersDialog({
  open,
  onClose,
}) {
  const [users, setUsers] = useState([]);

  const [summary, setSummary] =
    useState({
      totalUsers: 0,
      freeUsers: 0,
      silverUsers: 0,
      goldUsers: 0,
      registrationsToday: 0,
      registrationsLast7Days: 0,
      activeToday: 0,
      activeLast7Days: 0,
    });

  const [sortField, setSortField] =
    useState("registrationDate");

  const [sortDirection, setSortDirection] =
    useState("desc");

  const [selectedUserId, setSelectedUserId] =
    useState("");
  const [selectedUser, setSelectedUser] =
    useState(null);

  const [plan, setPlan] =
    useState("free");

  const [planStartedAt, setPlanStartedAt] =
    useState("");

  const [planExpiresAt, setPlanExpiresAt] =
    useState("");

  const [savingPlan, setSavingPlan] =
    useState(false);

  const [successMessage, setSuccessMessage] =
    useState("");

  const [loadingUsers, setLoadingUsers] =
    useState(false);
  const [loadingUser, setLoadingUser] =
    useState(false);

  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    async function loadUsers() {
      setLoadingUsers(true);
      setError("");

      try {
        const result =
          await callUserFunction(
            "get-all-users"
          );

        if (cancelled) {
          return;
        }

        setUsers(
          Array.isArray(result?.users)
            ? result.users
            : []
        );

        setSummary({
          totalUsers:
            Number(
              result?.summary?.totalUsers ??
              result?.length ??
              0
            ),
          freeUsers:
            Number(
              result?.summary?.freeUsers ??
              0
            ),
          silverUsers:
            Number(
              result?.summary?.silverUsers ??
              0
            ),
          goldUsers:
            Number(
              result?.summary?.goldUsers ??
              0
            ),
          registrationsToday:
            Number(
              result?.summary
                ?.registrationsToday ??
              0
            ),
          registrationsLast7Days:
            Number(
              result?.summary
                ?.registrationsLast7Days ??
              0
            ),
          activeToday:
            Number(
              result?.summary?.activeToday ??
              0
            ),
          activeLast7Days:
            Number(
              result?.summary
                ?.activeLast7Days ??
              0
            ),
        });
      } catch (err) {
        console.error(
          "[Admin Users] load users",
          err
        );

        if (!cancelled) {
          setError(
            "Users konnten nicht geladen werden."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingUsers(false);
        }
      }
    }

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [open]);

  function dateInputValue(value) {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date
      .toISOString()
      .slice(0, 10);
  }

  async function loadSelectedUser(
    targetUserId
  ) {
    setSelectedUserId(targetUserId);
    setSelectedUser(null);
    setError("");
    setSuccessMessage("");
    setPlan("free");
    setPlanStartedAt("");
    setPlanExpiresAt("");

    if (!targetUserId) {
      return;
    }

    setLoadingUser(true);

    try {
      const result =
        await callUserFunction(
          "admin-get-user",
          {
            target_user_id:
              targetUserId,
          }
        );

      if (!result?.success) {
        setError(
          result?.message ||
            "User konnte nicht geladen werden."
        );
        return;
      }

      setSelectedUser(result.user);

      setPlan(
        result.user?.plan ||
          "free"
      );

      setPlanStartedAt(
        dateInputValue(
          result.user?.planStartedAt
        )
      );

      setPlanExpiresAt(
        dateInputValue(
          result.user?.planExpiresAt
        )
      );
    } catch (err) {
      console.error(
        "[Admin Users] load user",
        err
      );

      setError(
        "User konnte nicht geladen werden."
      );
    } finally {
      setLoadingUser(false);
    }
  }

  async function handleSelectUser(event) {
    await loadSelectedUser(
      event.target.value
    );
  }

  async function handleSavePlan() {
    if (!selectedUserId) {
      return;
    }

    setSavingPlan(true);
    setError("");
    setSuccessMessage("");

    try {
      const result =
        await callUserFunction(
          "admin-update-user-plan",
          {
            target_user_id:
              selectedUserId,
            plan,
            plan_started_at:
              planStartedAt || null,
            plan_expires_at:
              planExpiresAt || null,
          }
        );

      if (!result?.success) {
        setError(
          result?.message ||
            "Plan konnte nicht gespeichert werden."
        );
        return;
      }

      setSelectedUser((current) => ({
        ...current,
        ...result.user,
      }));

      setPlan(
        result.user?.plan ||
          "free"
      );

      setPlanStartedAt(
        dateInputValue(
          result.user?.planStartedAt
        )
      );

      setPlanExpiresAt(
        dateInputValue(
          result.user?.planExpiresAt
        )
      );

      setSuccessMessage(
        "Plan wurde gespeichert."
      );
    } catch (err) {
      console.error(
        "[Admin Users] save plan",
        err
      );

      setError(
        "Plan konnte nicht gespeichert werden."
      );
    } finally {
      setSavingPlan(false);
    }
  }

  function displayDate(value) {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (
      Number.isNaN(date.getTime())
    ) {
      return String(value);
    }

    return date.toLocaleString();
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDirection(
        (current) =>
          current === "asc"
            ? "desc"
            : "asc"
      );
      return;
    }

    setSortField(field);

    setSortDirection(
      field === "registrationDate" ||
      field === "lastLogin"
        ? "desc"
        : "asc"
    );
  }

  function sortValue(user, field) {
    const value = user?.[field];

    if (
      field === "registrationDate" ||
      field === "lastLogin"
    ) {
      if (!value) {
        return 0;
      }

      const time =
        new Date(value).getTime();

      return Number.isNaN(time)
        ? 0
        : time;
    }

    return String(
      value || ""
    ).toLocaleLowerCase();
  }

  const sortedUsers = [...users].sort(
    (a, b) => {
      const aValue =
        sortValue(a, sortField);

      const bValue =
        sortValue(b, sortField);

      let result = 0;

      if (
        typeof aValue === "number" &&
        typeof bValue === "number"
      ) {
        result = aValue - bValue;
      } else {
        result =
          String(aValue).localeCompare(
            String(bValue)
          );
      }

      return sortDirection === "asc"
        ? result
        : -result;
    }
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Admin · Users
      </DialogTitle>

      <DialogContent>
        <Stack
          spacing={3}
          sx={{ mt: 1 }}
        >
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success">
              {successMessage}
            </Alert>
          )}

          {loadingUsers ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={24}
              />
              <Typography>
                Users werden geladen …
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {[
                  [
                    "Users total",
                    summary.totalUsers,
                  ],
                  [
                    "Free users",
                    summary.freeUsers,
                  ],
                  [
                    "Silver users",
                    summary.silverUsers,
                  ],
                  [
                    "Gold users",
                    summary.goldUsers,
                  ],
                  [
                    "Registrations today",
                    summary.registrationsToday,
                  ],
                  [
                    "Active today",
                    summary.activeToday,
                  ],
                  [
                    "Registrations last 7 days",
                    summary.registrationsLast7Days,
                  ],
                  [
                    "Active last 7 days",
                    summary.activeLast7Days,
                  ],
                ].map(([label, value]) => (
                  <Paper
                    key={label}
                    variant="outlined"
                    sx={{
                      p: 2,
                      minHeight: 110,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {label}
                    </Typography>

                    <Typography
                      variant="h4"
                      sx={{ mt: 0.5 }}
                    >
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 360 }}
              >
                <Table
                  stickyHeader
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "email"}
                          direction={
                            sortField === "email"
                              ? sortDirection
                              : "asc"
                          }
                          onClick={() =>
                            handleSort("email")
                          }
                        >
                          E-Mail
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>
                        <TableSortLabel
                          active={sortField === "name"}
                          direction={
                            sortField === "name"
                              ? sortDirection
                              : "asc"
                          }
                          onClick={() =>
                            handleSort("name")
                          }
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>
                        <TableSortLabel
                          active={
                            sortField ===
                            "registrationDate"
                          }
                          direction={
                            sortField ===
                            "registrationDate"
                              ? sortDirection
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(
                              "registrationDate"
                            )
                          }
                        >
                          Registration
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>
                        <TableSortLabel
                          active={
                            sortField ===
                            "lastLogin"
                          }
                          direction={
                            sortField ===
                            "lastLogin"
                              ? sortDirection
                              : "desc"
                          }
                          onClick={() =>
                            handleSort(
                              "lastLogin"
                            )
                          }
                        >
                          Last login
                        </TableSortLabel>
                      </TableCell>

                      <TableCell>
                        <TableSortLabel
                          active={sortField === "plan"}
                          direction={
                            sortField === "plan"
                              ? sortDirection
                              : "asc"
                          }
                          onClick={() =>
                            handleSort("plan")
                          }
                        >
                          Plan
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortedUsers.map((user) => (
                      <TableRow
                        hover
                        key={user.id || user.label}
                        selected={
                          selectedUserId ===
                          (user.id || user.label)
                        }
                        onClick={() =>
                          loadSelectedUser(
                            user.id || user.label
                          )
                        }
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        <TableCell>
                          {user.email ||
                            user.label ||
                            "—"}
                        </TableCell>

                        <TableCell>
                          {user.name || "—"}
                        </TableCell>

                        <TableCell>
                          {displayDate(
                            user.registrationDate
                          )}
                        </TableCell>

                        <TableCell>
                          {displayDate(
                            user.lastLogin
                          )}
                        </TableCell>

                        <TableCell>
                          {user.plan || "free"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TextField
                select
                fullWidth
                label="User"
                value={selectedUserId}
                onChange={handleSelectUser}
              >
                <MenuItem value="">
                  User auswählen
                </MenuItem>

                {users.map((user) => (
                  <MenuItem
                    key={user.id || user.label}
                    value={user.id || user.label}
                  >
                    {user.email || user.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {loadingUser && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress
                size={24}
              />
              <Typography>
                User wird geladen …
              </Typography>
            </Box>
          )}

          {selectedUser &&
            !loadingUser && (
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  {selectedUser.name ||
                    selectedUser.email ||
                    selectedUser.id}
                </Typography>

                <Stack spacing={1}>
                  <Typography>
                    <strong>ID:</strong>{" "}
                    {selectedUser.id}
                  </Typography>

                  <Typography>
                    <strong>
                      E-Mail:
                    </strong>{" "}
                    {selectedUser.email ||
                      "—"}
                  </Typography>

                  <Typography>
                    <strong>
                      Plan:
                    </strong>{" "}
                    {selectedUser.plan ||
                      "free"}
                  </Typography>

                  <Typography>
                    <strong>
                      Plan started at:
                    </strong>{" "}
                    {displayDate(
                      selectedUser.planStartedAt
                    )}
                  </Typography>

                  <Typography>
                    <strong>
                      Plan expires at:
                    </strong>{" "}
                    {displayDate(
                      selectedUser.planExpiresAt
                    )}
                  </Typography>

                  <Typography>
                    <strong>
                      Registration:
                    </strong>{" "}
                    {displayDate(
                      selectedUser.registrationDate
                    )}
                  </Typography>

                  <Typography>
                    <strong>
                      Last login:
                    </strong>{" "}
                    {displayDate(
                      selectedUser.lastLogin
                    )}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    mt: 3,
                    pt: 3,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Plan Administration
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      select
                      fullWidth
                      label="Plan"
                      value={plan}
                      onChange={(event) => {
                        const value =
                          event.target.value;

                        setPlan(value);
                        setSuccessMessage("");

                        if (value === "free") {
                          setPlanStartedAt("");
                          setPlanExpiresAt("");
                        }
                      }}
                    >
                      <MenuItem value="free">
                        Free
                      </MenuItem>
                      <MenuItem value="silver">
                        Silver
                      </MenuItem>
                      <MenuItem value="gold">
                        Gold
                      </MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      type="date"
                      label="Plan Started At"
                      value={planStartedAt}
                      disabled={plan === "free"}
                      onChange={(event) => {
                        setPlanStartedAt(
                          event.target.value
                        );
                        setSuccessMessage("");
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <TextField
                      fullWidth
                      type="date"
                      label="Plan Expires At"
                      value={planExpiresAt}
                      disabled={plan === "free"}
                      onChange={(event) => {
                        setPlanExpiresAt(
                          event.target.value
                        );
                        setSuccessMessage("");
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={handleSavePlan}
                      disabled={
                        savingPlan ||
                        (
                          plan !== "free" &&
                          !planStartedAt
                        )
                      }
                    >
                      {savingPlan
                        ? "Saving …"
                        : "Save Plan"}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
